import React from 'react';
import { ShopItem } from '../types';

interface ShopItemDetailModalProps {
  item: ShopItem | null;
  isOpen: boolean;
  onClose: () => void;
  onPurchase: (item: ShopItem) => void;
  canAfford: boolean;
}

const ShopItemDetailModal: React.FC<ShopItemDetailModalProps> = ({ 
  item, 
  isOpen, 
  onClose, 
  onPurchase,
  canAfford 
}) => {
  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-[100] p-3 sm:p-4 modal-backdrop animate-fade-in">
      <div className="relative max-w-sm sm:max-w-lg w-full animate-scale-in">
        {/* Background Image (if available) */}
        {item.image && (
          <div 
            className="absolute inset-0 rounded-xl opacity-20 blur-lg"
            style={{
              backgroundImage: `url(${item.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        )}

        {/* Main Modal */}
        <div 
          className="relative bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl shadow-2xl overflow-hidden"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Header - Fixed at top */}
          <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-b border-purple-500/20"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div>
              <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
                <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse"></div>
                <span className="text-[10px] sm:text-xs font-orbitron text-purple-400 tracking-wider uppercase">
                  ［ System Shop ］
                </span>
              </div>
              <h2 className="text-base sm:text-lg font-bold font-orbitron text-white tracking-wider">
                ITEM DETAILS
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-lg sm:text-xl leading-none transition-all bg-gradient-to-b from-slate-700/50 to-slate-800/50 rounded-full p-1.5 sm:p-2 border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60"
              style={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
              }}
            >
              ✕
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="p-3 sm:p-4 max-h-[calc(85vh-80px)] overflow-y-auto">
            {/* Item Icon/Image */}
            <div className="flex justify-center pb-2 sm:pb-3">
              {item.image ? (
                <div className="relative">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-16 h-16 sm:w-24 sm:h-24 object-contain"
                    style={{
                      filter: 'drop-shadow(0 0 40px rgba(168, 85, 247, 0.9)) brightness(1.1)'
                    }}
                  />
                </div>
              ) : (
                <div className="text-4xl sm:text-6xl drop-shadow-[0_0_20px_rgba(168,85,247,0.6)]">
                  {item.icon}
                </div>
              )}
            </div>

            {/* Item Name */}
            <h2 className="text-lg sm:text-xl font-bold font-orbitron text-center mb-1.5 sm:mb-2 px-3 sm:px-4 glow-text tracking-wider drop-shadow-[0_4px_8px_rgba(0,0,0,0.9)]">
              {item.name}
            </h2>

            {/* Legendary Tag (if in description) */}
            {item.description.includes('Legendary') && (
              <div className="flex justify-center mb-2 sm:mb-3">
                <span className="px-2.5 sm:px-3 py-0.5 sm:py-1 bg-gradient-to-b from-yellow-600/90 to-orange-600/90 text-white font-orbitron font-bold text-[10px] sm:text-xs rounded-full border-2 border-yellow-400/50 backdrop-blur-sm"
                  style={{
                    boxShadow: '0 2px 8px rgba(251, 191, 36, 0.4)'
                  }}
                >
                  「 Legendary Item 」
                </span>
              </div>
            )}

            {/* Description */}
            <div className="px-3 sm:px-4 mb-3 sm:mb-4">
              <div className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3"
                style={{
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <p className="text-[13px] sm:text-sm text-white font-rajdhani font-bold text-center leading-relaxed" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.9)' }}>
                  {item.description.replace('「Legendary Item」', '').trim()}
                </p>
              </div>
            </div>

            {/* Effects Section */}
            {item.effect && (
              <div className="px-3 sm:px-4 mb-3 sm:mb-4">
                <div className="bg-gradient-to-b from-purple-950/40 via-slate-950/30 to-purple-950/40 backdrop-blur-sm border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-2xl p-3 sm:p-4"
                  style={{
                    boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                  }}
                >
                  <div className="text-center mb-1.5 sm:mb-2">
                    <span className="font-orbitron text-purple-400 text-[10px] sm:text-xs tracking-wider uppercase">
                      ［ EFFECTS ］
                    </span>
                  </div>
                  <div className="space-y-1.5 sm:space-y-2">
                    {item.effect.xpMultiplier !== undefined && item.effect.xpMultiplier > 0 && (
                      <div className="flex items-center justify-between px-2.5 sm:px-3 py-1.5 sm:py-2 bg-blue-500/10 rounded-lg border-t border-l border-blue-500/30 border-r border-b border-blue-700/50 text-[13px] sm:text-sm">
                        <span className="text-blue-400 font-rajdhani font-semibold">Experience Points</span>
                        <span className="text-white font-orbitron font-bold">+{item.effect.xpMultiplier}</span>
                      </div>
                    )}
                    {item.effect.statBoost && Object.entries(item.effect.statBoost).map(([stat, value]) => (
                      <div key={stat} className="flex items-center justify-between px-2.5 sm:px-3 py-1.5 sm:py-2 bg-green-500/10 rounded-lg border-t border-l border-green-500/30 border-r border-b border-green-700/50 text-[13px] sm:text-sm">
                        <span className="text-green-400 font-rajdhani font-semibold">
                          {stat.charAt(0).toUpperCase() + stat.slice(1)}
                        </span>
                        <span className="text-white font-orbitron font-bold">+{value}</span>
                      </div>
                    ))}
                    {item.effect.currencyMultiplier && (
                      <div className="flex items-center justify-between px-2.5 sm:px-3 py-1.5 sm:py-2 bg-yellow-500/10 rounded-lg border-t border-l border-yellow-500/30 border-r border-b border-yellow-700/50 text-[13px] sm:text-sm">
                        <span className="text-yellow-400 font-rajdhani font-semibold">Currency Bonus</span>
                        <span className="text-white font-orbitron font-bold">+{item.effect.currencyMultiplier}%</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Price and Purchase Button */}
            <div className="px-3 sm:px-4 pb-3 sm:pb-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 sm:gap-3">
                {/* Price */}
                <div className="flex items-center gap-2">
                  <img 
                    src="/icons/icon-gold.png" 
                    alt="Gold"
                    className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                    style={{ filter: 'drop-shadow(0 0 12px rgba(252, 211, 77, 0.8))' }}
                  />
                  <span className="text-2xl sm:text-3xl font-black font-orbitron" style={{ color: '#FCD34D', textShadow: '0 0 20px rgba(252, 211, 77, 0.8)' }}>
                    {item.price}
                  </span>
                </div>

                {/* Purchase Button */}
                <button
                  onClick={() => canAfford && onPurchase(item)}
                  disabled={!canAfford}
                  className={`relative group w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 font-orbitron font-bold text-sm tracking-wider transition-all overflow-hidden text-white ${
                    canAfford 
                      ? 'bg-gradient-to-b from-blue-600/90 to-blue-700/90 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 hover:scale-105 rounded-2xl' 
                      : 'bg-gradient-to-b from-slate-700/60 via-slate-800/60 to-slate-900/60 opacity-50 cursor-not-allowed border-t-2 border-l-2 border-slate-700/70 border-r-2 border-b-2 border-r-slate-900/80 border-b-slate-950/80 rounded-2xl'
                  }`}
                  style={canAfford ? {
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                  } : {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                  }}
                >
                  {canAfford && (
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                  )}
                  <span className="relative z-10">{canAfford ? 'BUY' : 'LOCKED'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopItemDetailModal;

